import { VENUE_MANAGEMENT_ENABLED } from "./config/meetingLocationFeatures";
import VenueManagementPage from "./pages/venueManagement/VenueManagementPage";
import FeedPage from "./pages/dashboard/FeedPage";
import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import ProtectedRoute from "./lib/ProtectedRoute";

// FCM SYNC
import { messaging, getToken } from "./firebase-config";
import { updateFcmToken } from "./services/userService";
import { setFcmToken, logout, fetchUserData } from "./redux/userSlice";

// Pages
import LandingPage from "./pages/landingPage/LandingPage";
import OnboardingPage from "./pages/onboarding/getStartedPage";
import LocationSelectionPage from "./pages/onboarding/LocationSelectionPage";
import TeamSelectionStep from "./pages/onboarding/teamSelectionPage";
import DogDetailsPage from "./pages/onboarding/DogDetailsPage";
import DogPersonalityPage from "./pages/onboarding/DogPersonalityPage";
import TeamSocialPersonalityPage from "./pages/onboarding/teamSocialOnboarding/TeamSocialPersonalityPage";
import TeamSocialDimsumAvailabilityPage from "./pages/onboarding/teamSocialOnboarding/TeamSocialDimsumAvailabilityPage";
import TeamSocialExperiencePage from "./pages/onboarding/teamSocialOnboarding/TeamSocialExperiencePage";
import TeamSocialChecklistPage from "./pages/onboarding/teamSocialOnboarding/TeamSocialChecklistPage";
import TeamSocialBioPage from "./pages/onboarding/teamSocialOnboarding/TeamSocialBioPage";
import TeamPawsPersonalityPage from "./pages/onboarding/TeamPawsPersonalityPage";
import DimsumAvailabilityPage from "./pages/onboarding/DimsumAvailabilityPage";
import ExpWithDogsPage from "./pages/onboarding/ExpWithDogsPage";
import WriteDogBioPage from "./pages/onboarding/WriteDogBioPage";
import ExpWithHumansPage from "./pages/onboarding/ExpWithHumansPage";
import CompletionPage from "./pages/onboarding/CompletionPage";
import AppTourPage from "./pages/onboarding/AppTourPage";
import OnboardingTutorial from "./pages/onboarding/OnboardingTutorial";
import InstallPromptPage from "./pages/onboarding/InstallPromptPage";
import OnboardingTutorialSteps from "./pages/onboarding/OnboardingTutorialSteps";
import HalfOnboardingPage from "./pages/onboarding/HalfOnboardingPage";
import HalfCompletedPage from "./pages/onboarding/HalfCompletedPage";
import UserDetailsStepOnePage from "./pages/onboarding/UserDetailsStepOnePage";
import UserDetailsStepTwoPage from "./pages/onboarding/UserDetailsStepTwoPage";
import OnboardingFinalPage from "./pages/onboarding/OnboardingFinalPage";
import DashboardSwitcher from "./pages/dashboard/DashboardSwitcher";
import HelpPage from "./pages/dashboard/HelpPage";
import ProfileDetailsPage from "./pages/dashboard/ProfileDetailsPage";
import ReceiveATreatPage from "./pages/dashboard/notificationViews/ReceiveATreatPage";
import ReceiveABookPage from "./pages/dashboard/notificationViews/ReceiveABookPage";
import TodayWalkNotificationPage from "./pages/dashboard/notificationViews/TodayWalkNotificationPage";
import BookAWalkTeamPawsPage from "./pages/dashboard/BookAWalkTeamPawsPage";
import MessagesPage from "./pages/dashboard/MessagesPage";
import TeamPawsWalkFeedbackPage from "./pages/walkFeedback/TeamPawsWalkFeedbackPage";
import TeamPawsAvailabilityPage from "./pages/walkFeedback/TeamPawsAvailabilityPage";
import GetTeamPawsAvailabilityPage from "./pages/walkFeedback/GetTeamPawsAvailabilityPage";
import PassTheLeashPage from "./pages/walkFeedback/PassTheLeashPage";
import AvailabilityRequestsPage from "./pages/walkFeedback/AvailabilityRequestsPage";
import TeamSocialWalkFeedbackPage from "./pages/walkFeedback/TeamSocialWalkFeedbackPage";
import TakeTheLeashPage from "./pages/walkFeedback/TakeTheLeashPage";
import TeamSocialAvailabilityPage from "./pages/walkFeedback/TeamSocialAvailabilityPage";
import DogSharingJourneyPage from "./pages/dogSharing/DogSharingJourneyPage";
import WalkiesAndFoodPage from "./pages/pupCare/WalkiesAndFoodPage";
import PupEmergencyPlanPage from "./pages/pupCare/PupEmergencyPlanPage";
import HouseTrainingPage from "./pages/pupCare/HouseTrainingPage";
import LoginPage from "./pages/onboarding/loginPage/LoginPage";
import ForgotPasswordPage from "./pages/onboarding/loginPage/ForgotPasswordPage";
import VerifyCodePage from "./pages/onboarding/loginPage/VerifyCodePage";
import CreatePasswordPage from "./pages/onboarding/loginPage/CreatePasswordPage";
import PasswordUpdatedPage from "./pages/onboarding/loginPage/PasswordUpdatedPage";
import CurrentBookingsPage from "./pages/currentBookings/CurrentBookingsPage";
import Notifications from "./pages/dashboard/Notifications";
import UserProfileDetailsPage from "./pages/profile/UserProfileDetailsPage";
import SettingsPage from "./pages/profile/SettingsPage";
import SelectProfilePage from "./pages/profile/SelectProfilePage";
import DogDetailsEditPage from "./pages/profile/DogDetailsEditPage";
import DogPersonalityEditPage from "./pages/profile/DogPersonalityEditPage";
import TeamPawsPersonalityEditPage from "./pages/profile/TeamPawsPersonalityEditPage";
import DimsumAvailabilityEditPage from "./pages/profile/DimsumAvailabilityEditPage";
import ExpWithDogsEditPage from "./pages/profile/ExpWithDogsEditPage";
import ExpWithHumansEditPage from "./pages/profile/ExpWithHumansEditPage";
import WriteDogBioEditPage from "./pages/profile/WriteDogBioEditPage";
import UserProfileEditPage from "./pages/profile/UserProfileEditPage";
import UserDetailsEditTwoPage from "./pages/profile/UserDetailsEditTwoPage";
import ProfileCreatePasswordPage from "./pages/profile/ProfileCreatePasswordPage";
import WalkBuddyRating from "./pages/walkFeedback/WalkBuddyRating";
import VerifyEmailPage from "./pages/onboarding/VerifyEmailPage";
import UnsubscribePage from "./pages/unsubscribe/UnsubscribePage";

import { AlertProvider } from "./context/AlertContext";
import { NotificationProvider } from "./context/NotificationContext";

// MAIN APP COMPONENT
const App = () => {
  const dispatch = useDispatch();
  // Check if user has an active session persisted in Redux
  const { profile } = useSelector((state) => state.user);
  const token = useSelector((state) => state.user.token);
  // Get the userId to pass to the updateFcmToken API
  const userId = useSelector((state) => state.user.profile?.id);
  const isAuthenticated = Boolean(token);

  const firstDog = profile?.dogs?.[0];

  const isDogProfileComplete = firstDog?.isDogProfileComplete ?? 0;
  const isDogProfileHalfComplete = firstDog?.isDogProfileHalfComplete ?? 0;

  // Redirect only if either flag is 1
  const shouldRedirectToDashboard =
    isDogProfileComplete === 1 || isDogProfileHalfComplete === 1;

  // SESSION VERIFICATION ON MOUNT
  useEffect(() => {
    const verifyUserSession = async () => {
      if (isAuthenticated && userId) {
        try {
          // Fetch latest user data to confirm the profile still exists in the DB
          await dispatch(fetchUserData(userId)).unwrap();
        } catch (error) {
          // If the API returns status false or 404
          // Clear profile data, tokens, and session storage via your existing logout reducer
          console.warn("Session invalid or profile deleted. Logging out.");
          dispatch(logout());
        }
      }
    };

    verifyUserSession();
  }, [isAuthenticated, userId, dispatch]);

  // FCM TOKEN SYNC
  useEffect(() => {
    const syncFcmToken = async () => {
      // Only attempt to sync if the user is authenticated
      if (isAuthenticated && userId) {
        try {
          const registration = await navigator.serviceWorker.register(
            "/sw.js",
            { scope: "/" },
          );

          await navigator.serviceWorker.ready;

          const currentToken = await getToken(messaging, {
            vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
            serviceWorkerRegistration: registration,
          });

          if (currentToken) {
            // Update the token in Redux state
            dispatch(setFcmToken(currentToken));
            // Send the fresh token to the backend to reclaim notifications for this device
            await updateFcmToken(userId, currentToken);
          }
        } catch (error) {
          console.error("Failed to sync FCM token on App load:", error);
        }
      }
    };

    syncFcmToken();
  }, [isAuthenticated, userId, dispatch]);

  // Centralized Form Data
  const [formData, setFormData] = useState(() => {
    const savedData = localStorage.getItem("sp_onboarding_form");
    return savedData
      ? JSON.parse(savedData)
      : {
          name: "",
          email: "",
          password: "",
          confirmPassword: "",
          street: "",
          apartment: "",
          city: "",
          state: "",
          zip: "",
          team: "",
          dogName: "",
          dogBreed: "",
          selectedBreed1: "",
          selectedBreed2: "",
          selectedBreed3: "",
          dogGender: "",
          dogBirthday: "",
          dogAgeGroup: "",
          dogActivity: "",
          selectedPromptIndex: 0,
          dogs: [],
        };
  });

  useEffect(() => {
    localStorage.setItem("sp_onboarding_form", JSON.stringify(formData));
  }, [formData]);

  const [filterData, setFilterData] = useState({
    teamSelection: "both",
    proximityValue: 5,
    availability: [""],
    breed: "",
    experience: [""],
      age: "",
  });
  const [formDataEmail, setFormDataEmail] = useState({ email: "" });

  return (
    <AlertProvider>
      <NotificationProvider>
        <div>
          <Routes>
            {/* PUBLIC ROUTES WITH AUTH CHECKS */}
            <Route
              path="/"
              element={
                isAuthenticated && shouldRedirectToDashboard ? (
                  <Navigate to="/dashboard-switcher" replace />
                ) : (
                  <LandingPage formData={formData} setFormData={setFormData} />
                )
              }
            />
            {/* <Route path="/login" element={<LoginPage />} /> */}
            {/* <Route
              path="/forgot-password"
              element={
                <ForgotPasswordPage
                  formDataEmail={formDataEmail}
                  setFormDataEmail={setFormDataEmail}
                />
              }
            /> */}
            <Route
              path="/verification"
              element={
                <VerifyCodePage
                  formDataEmail={formDataEmail}
                  setFormDataEmail={setFormDataEmail}
                />
              }
            />
            <Route path="/create-password" element={<CreatePasswordPage />} />
            <Route
              path="/password-updated"
              element={
                <PasswordUpdatedPage
                  formDataEmail={formDataEmail}
                  setFormDataEmail={setFormDataEmail}
                />
              }
            />
            <Route
              path="/onboarding"
              element={
                <LocationSelectionPage
                  formData={formData}
                  setFormData={setFormData}
                />
              }
            />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route
              path="/team-selection"
              element={
                <TeamSelectionStep
                  formData={formData}
                  setFormData={setFormData}
                />
              }
            />
            <Route
              path="/dog-details"
              element={
                <DogDetailsPage formData={formData} setFormData={setFormData} />
              }
            />
            <Route
              path="/dog-personality"
              element={
                <DogPersonalityPage
                  formData={formData}
                  setFormData={setFormData}
                />
              }
            />
            <Route
              path="/dog-social-personality"
              element={
                <TeamSocialPersonalityPage
                  formData={formData}
                  setFormData={setFormData}
                />
              }
            />
            <Route
              path="/team-social-experience"
              element={
                <TeamSocialExperiencePage
                  formData={formData}
                  setFormData={setFormData}
                />
              }
            />
            <Route
              path="/team-social-dimsum-availability"
              element={
                <TeamSocialDimsumAvailabilityPage
                  formData={formData}
                  setFormData={setFormData}
                />
              }
            />
            <Route
              path="/team-social-checklist"
              element={
                <TeamSocialChecklistPage
                  formData={formData}
                  setFormData={setFormData}
                />
              }
            />
            <Route
              path="/team-social-bio"
              element={
                <TeamSocialBioPage
                  formData={formData}
                  setFormData={setFormData}
                />
              }
            />
            <Route
              path="/team-paws-personality"
              element={
                <TeamPawsPersonalityPage
                  formData={formData}
                  setFormData={setFormData}
                />
              }
            />
            <Route
              path="/dimsum-availability"
              element={
                <DimsumAvailabilityPage
                  formData={formData}
                  setFormData={setFormData}
                />
              }
            />
            <Route
              path="/exp-with-dogs"
              element={
                <ExpWithDogsPage
                  formData={formData}
                  setFormData={setFormData}
                />
              }
            />
            <Route
              path="/exp-with-humans"
              element={
                <ExpWithHumansPage
                  formData={formData}
                  setFormData={setFormData}
                />
              }
            />
            <Route
              path="/write-dog-bio"
              element={
                <WriteDogBioPage
                  formData={formData}
                  setFormData={setFormData}
                />
              }
            />
            <Route
              path="/completion-page"
              element={
                <CompletionPage formData={formData} setFormData={setFormData} />
              }
            />
            <Route
              path="/app-tour-page"
              element={
                <AppTourPage formData={formData} setFormData={setFormData} />
              }
            />
            <Route
              path="/onboarding-tutorial"
              element={
                <OnboardingTutorial
                  formData={formData}
                  setFormData={setFormData}
                />
              }
            />
            <Route
              path="/install-prompt-page"
              element={
                <InstallPromptPage
                  formData={formData}
                  setFormData={setFormData}
                />
              }
            />
            <Route
              path="/onboarding-tutorial-steps"
              element={
                <OnboardingTutorialSteps
                  formData={formData}
                  setFormData={setFormData}
                />
              }
            />
            <Route
              path="/half-onboarding-page"
              element={
                <HalfOnboardingPage
                  formData={formData}
                  setFormData={setFormData}
                />
              }
            />
            <Route
              path="/half-completed-page"
              element={
                <HalfCompletedPage
                  formData={formData}
                  setFormData={setFormData}
                />
              }
            />
            <Route
              path="/user-details-step-one"
              element={
                <UserDetailsStepOnePage
                  formData={formData}
                  setFormData={setFormData}
                />
              }
            />
            <Route
              path="/user-details-step-two"
              element={
                <UserDetailsStepTwoPage
                  formData={formData}
                  setFormData={setFormData}
                />
              }
            />
            <Route
              path="/onboarding-final"
              element={
                <OnboardingFinalPage
                  formData={formData}
                  setFormData={setFormData}
                />
              }
            />

            {/* PROTECTED DASHBOARD & FEATURES */}
            <Route path="/venue-management" element={<ProtectedRoute>{VENUE_MANAGEMENT_ENABLED ? <VenueManagementPage /> : <Navigate to="/dashboard-switcher" replace />}</ProtectedRoute>} />
            <Route path="/feed" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />
            <Route
              path="/dashboard-switcher"
              element={
                <ProtectedRoute>
                  <DashboardSwitcher
                    filterData={filterData}
                    setFilterData={setFilterData}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/help"
              element={
                <ProtectedRoute>
                  <HelpPage
                    filterData={filterData}
                    setFilterData={setFilterData}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile-details"
              element={
                <ProtectedRoute>
                  <ProfileDetailsPage
                    filterData={filterData}
                    setFilterData={setFilterData}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/receive-a-treat"
              element={
                <ProtectedRoute>
                  <ReceiveATreatPage
                    filterData={filterData}
                    setFilterData={setFilterData}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/receive-a-book"
              element={
                <ProtectedRoute>
                  <ReceiveABookPage
                    filterData={filterData}
                    setFilterData={setFilterData}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/today-walk-notification"
              element={
                <ProtectedRoute>
                  <TodayWalkNotificationPage
                    filterData={filterData}
                    setFilterData={setFilterData}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/book-a-walk-team-paws"
              element={
                <ProtectedRoute>
                  <BookAWalkTeamPawsPage
                    filterData={filterData}
                    setFilterData={setFilterData}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <ProtectedRoute>
                  <MessagesPage
                    filterData={filterData}
                    setFilterData={setFilterData}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/current-bookings"
              element={
                <ProtectedRoute>
                  <CurrentBookingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/walkies-and-food"
              element={
                <ProtectedRoute>
                  <WalkiesAndFoodPage
                    formData={formData}
                    setFormData={setFormData}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pup-emergency-plan"
              element={
                <ProtectedRoute>
                  <PupEmergencyPlanPage
                    formData={formData}
                    setFormData={setFormData}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/house-training"
              element={
                <ProtectedRoute>
                  <HouseTrainingPage
                    formData={formData}
                    setFormData={setFormData}
                  />
                </ProtectedRoute>
              }
            />

            {/* Feedback Routes */}
            <Route
              path="/team-paws-post-walk-feedback"
              element={
                <ProtectedRoute>
                  <TeamPawsWalkFeedbackPage
                    filterData={filterData}
                    setFilterData={setFilterData}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/team-paws-availability"
              element={
                <ProtectedRoute>
                  <TeamPawsAvailabilityPage
                    filterData={filterData}
                    setFilterData={setFilterData}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/team-social-availability"
              element={
                <ProtectedRoute>
                  <GetTeamPawsAvailabilityPage
                    filterData={filterData}
                    setFilterData={setFilterData}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pass-the-leash"
              element={
                <ProtectedRoute>
                  <PassTheLeashPage
                    filterData={filterData}
                    setFilterData={setFilterData}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/availability-request"
              element={
                <ProtectedRoute>
                  <AvailabilityRequestsPage
                    filterData={filterData}
                    setFilterData={setFilterData}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/team-social-post-walk-feedback"
              element={
                <ProtectedRoute>
                  <TeamSocialWalkFeedbackPage
                    filterData={filterData}
                    setFilterData={setFilterData}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/team-social-availability"
              element={
                <ProtectedRoute>
                  <TeamSocialAvailabilityPage
                    filterData={filterData}
                    setFilterData={setFilterData}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/take-the-leash"
              element={
                <ProtectedRoute>
                  <TakeTheLeashPage
                    filterData={filterData}
                    setFilterData={setFilterData}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dog-sharing-journey"
              element={
                <ProtectedRoute>
                  <DogSharingJourneyPage
                    filterData={filterData}
                    setFilterData={setFilterData}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <Notifications
                    filterData={filterData}
                    setFilterData={setFilterData}
                  />
                </ProtectedRoute>
              }
            />

            {/* Profile Edit Routes */}
            <Route
              path="/user-profile"
              element={
                <ProtectedRoute>
                  <UserProfileDetailsPage setFormData={setFormData} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings-page"
              element={
                <ProtectedRoute>
                  <SettingsPage setFormData={setFormData} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/select-edit-profile"
              element={
                <ProtectedRoute>
                  <SelectProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-dog-details"
              element={
                <ProtectedRoute>
                  <DogDetailsEditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-dog-personality"
              element={
                <ProtectedRoute>
                  <DogPersonalityEditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-team-paws-personality"
              element={
                <ProtectedRoute>
                  <TeamPawsPersonalityEditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-dimsum-availability"
              element={
                <ProtectedRoute>
                  <DimsumAvailabilityEditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-exp-with-dogs"
              element={
                <ProtectedRoute>
                  <ExpWithDogsEditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-exp-with-humans"
              element={
                <ProtectedRoute>
                  <ExpWithHumansEditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-dog-bio"
              element={
                <ProtectedRoute>
                  <WriteDogBioEditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-user-details"
              element={
                <ProtectedRoute>
                  <UserProfileEditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-user-details-two"
              element={
                <ProtectedRoute>
                  <UserDetailsEditTwoPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile-change-password"
              element={
                <ProtectedRoute>
                  <ProfileCreatePasswordPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/walk-buddy-rating"
              element={
                <ProtectedRoute>
                  <WalkBuddyRating />
                </ProtectedRoute>
              }
            />

            <Route path="/unsubscribe" element={<UnsubscribePage />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </NotificationProvider>
    </AlertProvider>
  );
};

export default App;
